import React, { useState, useEffect, useCallback } from 'react';

import cn from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { Image } from '@shared/ui';
import { EmblaControlButtons } from '@shared/ui/EmblaCarousel/EmblaControlButtons/EmblaControlButtons';

import cls from './EmblaCarousel.module.css';
import { Thumb } from './Thumb/Thumb';

interface IPreviewFile {
    id: number;
    fileSrc: string;
    description?: string;
    author?: {
        name: string;
        avatarUrl: string;
    };
    fileName?: string;
    mimeType?: string;
}

type LightBoxPayload = {
    id: string | number;
    newFiles: IPreviewFile[];
};

type PropType = {
    slides: {
        id: number | string;
        image: string;
        descriptionSlot?: React.ReactNode;
    }[];
    options?: EmblaOptionsType;
    slideHeight?: number;
    nextIcon?: React.ReactNode;
    prevIcon?: React.ReactNode;
    onShowLightBox?: ({ id, newFiles }: LightBoxPayload) => void;
    onThumbClick?: ({ id, newFiles }: LightBoxPayload) => void;
    files?: {
        id: number;
        fileSrc: string;
        description?: string;
        author?: {
            name: string;
            avatarUrl: string;
        };
        fileName?: string;
        mimeType?: string;
    }[];
    EmblaSlideClassName?: string;
    EmblaSlideNumberClassName?: string;
    SlideImgClassName?: string;
};

export const EmblaCarousel: React.FC<PropType> = (props) => {
    const { slides, options, slideHeight, prevIcon, nextIcon, onShowLightBox, files } = props;
    const safeSlides = slides ?? [];

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options);
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
    });

    const onSelect = useCallback(() => {
        if (!emblaMainApi || !emblaThumbsApi) return;
        setSelectedIndex(emblaMainApi.selectedScrollSnap());
        emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
    }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

    useEffect(() => {
        if (!emblaMainApi) return;
        onSelect();

        emblaMainApi.on('select', onSelect).on('reInit', onSelect);
    }, [emblaMainApi, onSelect]);

    return (
        <div className={cls.embla}>
            <div className={cls.emblaViewport} ref={emblaMainRef}>
                <div className={cls.emblaContainer}>
                    {safeSlides.map(({ image, descriptionSlot, id }) => (
                        <div
                            className={cn(cls.emblaSlide, props?.EmblaSlideClassName)}
                            key={image}
                            onClick={() => onShowLightBox?.({ id, newFiles: files ?? [] })}
                        >
                            {descriptionSlot}
                            <div
                                className={cn(
                                    cls.emblaSlideNumber,
                                    props.EmblaSlideNumberClassName
                                )}
                                // style={{ ['--bg-image' as string]: `url(${image})` }}
                            >
                                <Image
                                    src={image}
                                    alt=""
                                    wrapperClassName={cn(cls.SlideImg, props.SlideImgClassName)}
                                    style={{ height: slideHeight }}
                                />
                                {/* <img src={image} alt="" style={{ height: slideHeight }} /> */}
                            </div>
                        </div>
                    ))}
                </div>
                {safeSlides.length > 1 && (
                    <EmblaControlButtons
                        nextIcon={nextIcon}
                        prevIcon={prevIcon}
                        selectedIndex={selectedIndex}
                        slides={safeSlides}
                        emblaMainApi={emblaMainApi}
                        emblaThumbsApi={emblaThumbsApi}
                    />
                )}
            </div>
            {safeSlides.length > 1 && (
                <div className={cls.emblaThumbs}>
                    <div className={cls.emblaThumbsViewport} ref={emblaThumbsRef}>
                        <div className={cls.emblaThumbsContainer}>
                            {safeSlides.map((slide, index) => (
                                <Thumb
                                    key={slide.id}
                                    emblaMainApi={emblaMainApi}
                                    emblaThumbsApi={emblaThumbsApi}
                                    index={index}
                                    selected={index === selectedIndex}
                                    photo={slide.image}
                                    onThumbClick={() =>
                                        props.onThumbClick?.({
                                            id: slide.id,
                                            newFiles: files ?? [],
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
